import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import {
  Check,
  Copy,
  Info,
  Loader2,
  Lock,
  Pencil,
  ShieldCheck,
} from "lucide-react";
import { BankDetail } from "../../types/supplier";
import { toast } from "react-toastify";
import apiService from "../../services/apiService";

interface EditBankDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bankDetails: BankDetail;
  onUpdate: (bankDetails: BankDetail) => void;
  supplierId: string;
}

type VerifyData = {
  account_status?: string;
  name_at_bank?: string;
  bank_name?: string;
  branch_name?: string;
  utr?: string;
  account_number?: string;
  account_holder_name?: string;
  ifsc_code?: string;
};

const inputCls =
  "w-full h-10 px-3 rounded-[8px] border border-[#dde3e4] bg-white text-[13px] font-medium text-[#151b1d] placeholder:text-[#7c8b91] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none";
const labelCls = "block text-[12.5px] font-semibold text-[#151b1d] mb-1.5";

export function EditBankDetailsDialog({
  open,
  onOpenChange,
  bankDetails,
  onUpdate,
  supplierId,
}: EditBankDetailsDialogProps) {
  const [holderName, setHolderName] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [confirmAccountNo, setConfirmAccountNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [verified, setVerified] = useState(false);
  const [verifyData, setVerifyData] = useState<VerifyData | null>(null);
  const [verifiedAt, setVerifiedAt] = useState("");
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setHolderName("");
    setAccountNo("");
    setConfirmAccountNo("");
    setIfsc("");
    setVerified(false);
    setVerifyData(null);
    setVerifiedAt("");
    setValidating(false);
    setSaving(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    setHolderName(bankDetails.account_holder_name || "");
    setAccountNo(bankDetails.account_number || "");
    setConfirmAccountNo(bankDetails.account_number || "");
    setIfsc(bankDetails.ifsc_code || "");
    setVerified(false);
    setVerifyData(null);
    setVerifiedAt("");
  }, [open, bankDetails]);

  const accountsMatch =
    accountNo.length > 0 && confirmAccountNo.length > 0 && accountNo === confirmAccountNo;
  const accountMismatch =
    accountNo.length > 0 && confirmAccountNo.length > 0 && accountNo !== confirmAccountNo;
  const canValidate =
    !!holderName.trim() &&
    accountsMatch &&
    /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc) &&
    !validating &&
    !verified;

  const clearVerification = () => {
    setVerified(false);
    setVerifyData(null);
    setVerifiedAt("");
  };

  const handleValidate = async () => {
    if (!canValidate) {
      if (accountMismatch) toast.error("Account numbers do not match");
      else toast.error("Please fill all required fields correctly");
      return;
    }
    setValidating(true);
    try {
      const response: any = await apiService.post("/supplier/verify-supplier-bank", {
        supplier_id: Number(supplierId) || supplierId,
        custName: holderName.trim(),
        custAcctNo: accountNo,
        custIfsc: ifsc,
      });
      const data = (response?.data ?? response) as VerifyData;
      setVerifyData(data);
      setVerified(true);
      setVerifiedAt(
        new Date().toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );
      if (data.account_holder_name) setHolderName(data.account_holder_name);
      if (data.account_number) {
        setAccountNo(data.account_number);
        setConfirmAccountNo(data.account_number);
      }
      if (data.ifsc_code) setIfsc(data.ifsc_code);
      toast.success(response?.message || "Bank verification completed");
    } catch (err: any) {
      clearVerification();
      toast.error(err?.message || "Bank verification failed");
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    if (!verified || !verifyData) {
      toast.error("Please validate the account first");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        supplier_id: Number(supplierId) || supplierId,
        account_number: verifyData.account_number || accountNo,
        account_holder_name: verifyData.account_holder_name || holderName.trim(),
        ifsc_code: verifyData.ifsc_code || ifsc,
        bank_name: verifyData.bank_name || "",
        branch_name: verifyData.branch_name || "",
        name_at_bank: verifyData.name_at_bank || "",
        utr: verifyData.utr || "",
      };
      const response: any = await apiService.post("/supplier/save-supplier-bank", payload);
      const saved = (response?.data ?? payload) as VerifyData & {
        bank_name?: string;
        branch_name?: string;
      };
      onUpdate({
        bank_name: saved.bank_name || payload.bank_name,
        account_number: saved.account_number || payload.account_number,
        account_holder_name: saved.account_holder_name || payload.account_holder_name,
        ifsc_code: saved.ifsc_code || payload.ifsc_code,
        branch_name: saved.branch_name || payload.branch_name,
        name_at_bank: saved.name_at_bank || payload.name_at_bank,
        utr: saved.utr || payload.utr,
        account_status: verifyData.account_status || "Valid",
        bank_verified_at: verifiedAt || null,
      });
      toast.success(response?.message || "Supplier bank details saved successfully");
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Failed to save bank details");
    } finally {
      setSaving(false);
    }
  };

  const copyUtr = () => {
    if (!verifyData?.utr) return;
    void navigator.clipboard.writeText(verifyData.utr).then(() => {
      toast.success("UTR copied");
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] w-[95vw] max-h-[92vh] overflow-y-auto rounded-[14px] border border-[#dde3e4] bg-white p-0 shadow-2xl font-['Poppins',sans-serif] text-[#151b1d]">
        <div className="px-6 pt-5 pb-4 border-b border-[#eaeeef]">
          <h2 className="text-[17px] font-semibold text-[#151b1d]">Add Bank Account</h2>
          <p className="text-[12px] text-[#526066] mt-0.5">
            Validate the account first, then save it for payouts
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>
              Account Holder Name <span className="text-[#b0453f]">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Name as per bank records"
                value={holderName}
                disabled={verified || validating}
                onChange={(e) => {
                  setHolderName(e.target.value);
                  clearVerification();
                }}
                className={`${inputCls} ${verified ? "bg-[#f6f8f8] pr-9" : ""}`}
              />
              {verified && (
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#7c8b91]" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>
                Enter Account Number <span className="text-[#b0453f]">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Account number"
                  value={accountNo}
                  disabled={verified || validating}
                  maxLength={18}
                  onChange={(e) => {
                    setAccountNo(e.target.value.replace(/\D/g, ""));
                    clearVerification();
                  }}
                  className={`${inputCls} ${verified ? "bg-[#f6f8f8] pr-9" : ""}`}
                />
                {verified && (
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#7c8b91]" />
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>
                Re-enter Account Number <span className="text-[#b0453f]">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Type it again to confirm"
                  value={confirmAccountNo}
                  disabled={verified || validating}
                  maxLength={18}
                  onChange={(e) => {
                    setConfirmAccountNo(e.target.value.replace(/\D/g, ""));
                    clearVerification();
                  }}
                  className={`${inputCls} ${
                    accountMismatch ? "border-[#b0453f]" : ""
                  } ${verified || accountsMatch ? "pr-9" : ""} ${
                    verified ? "bg-[#f6f8f8]" : ""
                  }`}
                />
                {accountsMatch && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#227a4c]" />
                )}
              </div>
              <p className={`text-[11px] mt-1 ${accountMismatch ? "text-[#b0453f]" : "text-[#7c8b91]"}`}>
                {accountMismatch ? "Account numbers do not match" : "Must match the account number"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className={labelCls}>
                IFSC Code <span className="text-[#b0453f]">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="11-character IFSC"
                  value={ifsc}
                  disabled={verified || validating}
                  maxLength={11}
                  onChange={(e) => {
                    setIfsc(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11));
                    clearVerification();
                  }}
                  className={`${inputCls} uppercase ${verified ? "bg-[#f6f8f8] pr-9" : ""}`}
                />
                {verified && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#227a4c]" />
                )}
              </div>
              <p className="text-[11px] text-[#7c8b91] mt-1">
                Found on the cheque leaf or passbook
              </p>
            </div>

            {verified ? (
              <button
                type="button"
                onClick={clearVerification}
                className="h-10 px-3.5 rounded-[8px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-[13px] font-medium inline-flex items-center gap-1.5 cursor-pointer"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit details
              </button>
            ) : (
              <button
                type="button"
                disabled={!canValidate}
                onClick={() => void handleValidate()}
                className="h-10 px-4 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {validating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                {validating ? "Validating…" : "Validate account"}
              </button>
            )}
          </div>

          {!verified ? (
            <div className="rounded-[8px] border border-[#b3d9de] bg-[#e1f0f1] px-3.5 py-3 text-[12.5px] text-[#2e6b76] flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                We send ₹1 to the account to confirm it is live and that the name at the bank
                matches. The result shows here before you save.
              </span>
            </div>
          ) : (
            <div className="rounded-[10px] border border-[#bfe9cf] bg-[#e7f8ee] overflow-hidden">
              <div className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-[#227a4c] text-white flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                  </span>
                  <div>
                    <p className="text-[14px] font-semibold text-[#227a4c]">Status: Success</p>
                    <p className="text-[12px] text-[#526066] mt-0.5">
                      Penny-drop verification completed
                      {verifiedAt ? ` · ${verifiedAt}` : ""}
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#227a4c]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1e9a52]" />
                  Verified
                </span>
              </div>

              <div className="bg-white/70 border-t border-[#bfe9cf] px-4 py-1">
                {[
                  ["Account validation status", verifyData?.account_status || "Valid"],
                  ["Name at the bank", verifyData?.name_at_bank || "—"],
                  ["Bank name", verifyData?.bank_name || "—"],
                  ["Branch name", verifyData?.branch_name || "—"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-4 py-2.5 border-b border-[#eaeeef] last:border-b-0"
                  >
                    <span className="text-[12.5px] text-[#526066]">{label}</span>
                    <span className="text-[12.5px] font-semibold text-[#151b1d] text-right">
                      {value}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <span className="text-[12.5px] text-[#526066]">UTR</span>
                  <span className="text-[12.5px] font-semibold text-[#151b1d] inline-flex items-center gap-1.5">
                    {verifyData?.utr || "—"}
                    {verifyData?.utr && (
                      <button
                        type="button"
                        onClick={copyUtr}
                        className="text-[#526066] hover:text-[#3f8b98] cursor-pointer"
                        title="Copy UTR"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#eaeeef] flex items-center justify-between gap-3">
          {verified ? (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#227a4c]">
              <Check className="w-3.5 h-3.5" />
              Account verified.
            </span>
          ) : (
            <span className="text-[12px] text-[#7c8b91]">
              <span className="text-[#b0453f]">*</span> Required
            </span>
          )}

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={validating || saving}
              className="h-9 px-4 rounded-[8px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-[13px] font-medium cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!verified || saving}
              onClick={() => void handleSave()}
              className="h-9 px-5 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? "Saving…" : "Save bank account"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
